import { type ReactNode, useCallback } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import {
  type DashboardSettingsResponseDto,
  DashboardSettingsService,
  MessageSort,
} from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { queryClient } from '@/consts/query.ts'
import { $creatorId } from '@/models/creators.ts'
import {
  finishFanNumberingUpdating,
  startFanNumberingUpdating,
} from '@/modules/automations/models/fan-numbering.ts'
import { apiClient } from '@/utils/axios'
import { handleError } from '@/utils/error.ts'

import { DEFAULT_FAN_NUMBER } from '../constants.ts'

interface SettingsContextItems extends DashboardSettingsResponseDto {
  isFetching: boolean
  isLoading: boolean
  setSettingsData: (
    func: (
      prevState: DashboardSettingsResponseDto,
    ) => DashboardSettingsResponseDto,
  ) => void
  setSortValue: (value: Partial<SettingSortOptions>) => void
  settingsSortOptions: SettingSortOptions
}

export const SettingsContext = createContext<SettingsContextItems | null>(null)

const initialData: DashboardSettingsResponseDto = {
  autoFollowExpiredFans: true,
  maxFanNumber: DEFAULT_FAN_NUMBER,
  multipleBulkMessagesAllowed: false,
  bulkMessagesGlobalCooldown: 0,
  canLink: false,
  promotionReactivator: true,
  fanNumberingEnabled: true,
  defaultPromotionFinishDays: 1,
  countdownSoundVolume: 20,
  countdownSoundType: 'alarm_1',
  emojiTaskbar: '😄',
  // @ts-ignore
  colors: null,
  autoFollowMinimumSpend: 0,
  browsers: [],
  defaultColor: { backgroundColorHex: '', colorHex: '', id: 0 },
  emojiTaskbarEnabled: false,
  linkUrl: '',
  newSubsListId: 0,
  newSubsListName: '',
  welcomeMessages: '',
  ppvFollowUpMessages: [],
  ppvFollowUpsEnabled: false,
  autoMessageExpiringFans: {
    enabled: false,
    fallbackName: '',
    hoursBeforeExpiry: 0,
    maxSpend: 0,
    message: '',
    messageMediaId: 0,
    minHoursSinceLastMessage: 0,
  },
  ppvFollowUpsNotSeen: false,
  ppvFollowUpsBoughtNotReplied: false,
  scriptCategories: [],
  scripts: [],
  sendPpvFollowUpsAfterMinutes: 0,
  sendWelcomeMessagesAfterMinutes: 0,
  welcomeMessagesMedia: {},
  welcomeMessagingEnabled: false,
  showFanDetails: false,
  showScripts: false,
  showGlobalInfo: false,
  showFanSpending: false,
  showBiggerVault: false,
  autoUnfollowEnabled: false,
  autoUnfollowMaximumSpend: 0,
  autoUnfollowAfterFollowingDays: 0,
  customNotePresetEnabled: false,
  rebillMessages: [],
  autoMessageRebilledFansSettings: {
    enabled: false,
    minHoursSinceLastMessage: 24,
    sendAfterMinutes: 5,
  },
  messageMinPrice: 3,
  messageMaxPrice: 200,
  aiTranslationEnabled: true,
  fanNumberingUpdating: false,
}

export type ExtensionFeatureStateType =
  | 'showFanDetails'
  | 'showScripts'
  | 'showGlobalInfo'
  | 'showFanSpending'
  | 'showBiggerVault'

const extensionFeatureNames: Record<ExtensionFeatureStateType, string> = {
  showFanDetails: 'ShowFanDetails',
  showScripts: 'ShowScripts',
  showGlobalInfo: 'ShowGlobalInfo',
  showFanSpending: 'ShowFanSpending',
  showBiggerVault: 'ShowBiggerVault',
}

export interface SettingSortOptions {
  ppvFollowUpMessageSortBy: MessageSort
  expiringMessageSortBy: MessageSort
}

export function useExtensionFeatureChange() {
  const creatorId = useUnit($creatorId)

  const client = useQueryClient()
  const { settingsSortOptions } = useSettingsContext()

  return useMutation(
    (update: { feature: ExtensionFeatureStateType; state: boolean }) => {
      return apiClient.post(`/extension-features`, {
        creatorId,
        feature: extensionFeatureNames[update.feature],
        state: update.state,
      })
    },
    {
      onMutate: (update) => {
        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (oldData) => {
            if (!oldData) {
              return oldData
            }

            return {
              ...oldData,
              [update.feature]: update.state,
            }
          },
        )
      },
    },
  )
}

export const SettingsContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [sortOptions, setSortOptions] = useState<SettingSortOptions>({
    ppvFollowUpMessageSortBy: MessageSort.DEFAULT,
    expiringMessageSortBy: MessageSort.DEFAULT,
  })

  const creatorId = useUnit($creatorId)

  const {
    isFetching,
    isIdle,
    isLoading,
    data = initialData,
  } = useQuery(
    // Will be fetched every time activeCreator changes
    ['settings', creatorId, sortOptions],
    () =>
      DashboardSettingsService.getDashboardSettings({
        creatorId: creatorId!,
        ppvFollowUpMessageSortBy: sortOptions.ppvFollowUpMessageSortBy,
      }),
    {
      enabled: !!creatorId,
      onSuccess: ({ fanNumberingUpdating }) => {
        if (fanNumberingUpdating) {
          startFanNumberingUpdating()
        } else {
          finishFanNumberingUpdating()
        }
      },
      onError: (error) => {
        handleError(error, 'Failed to get settings')
      },
    },
  )

  const updateSortValue = useCallback((value: Partial<SettingSortOptions>) => {
    setSortOptions((prev) => {
      return {
        ...prev,
        ...value,
      }
    })
  }, [])

  const setSettingsData: SettingsContextItems['setSettingsData'] = useCallback(
    (func) => {
      if (creatorId) {
        queryClient.setQueryData<DashboardSettingsResponseDto>(
          ['settings', creatorId, sortOptions],
          (settings) => {
            return func(settings!)
          },
        )
      }
    },
    [creatorId, sortOptions],
  )

  const value: SettingsContextItems = useMemo(
    () => ({
      ...data,
      settingsSortOptions: sortOptions,
      isFetching: isIdle || isFetching,
      isLoading,
      setSortValue: updateSortValue,
      setSettingsData,
    }),
    [
      data,
      sortOptions,
      isIdle,
      isFetching,
      isLoading,
      updateSortValue,
      setSettingsData,
    ],
  )

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}

export const useSettingsContext = () => {
  const context = useContext(SettingsContext)

  if (!context) {
    throw new Error(
      'useSettingsContext must be used within the SettingsContextProvider.',
    )
  }

  return context
}
